import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import ReactSwitch from 'react-switch';
import { useAssetSchemasListQuery } from 'src/api/queries/schemesQueries';
import { AssetSchema, AssetSchemaProperty } from 'src/types/TSchemes';
import { Input, Select, Checkbox } from 'src/components';
import { CaretDown, CaretRight } from 'src/common/icons';
import styles from './styles.module.scss';

type EventKey = 'file-saved' | 'file-data-updated';

type SchemaCriteria = {
  data_schema_id?: number;
  fields?: Record<string, any>;
};

type EventConfig = {
  enabled?: boolean;
  content_types?: string[];
  schema_criteria?: SchemaCriteria;
};

type Disposition = {
  update_metadata?: boolean;
  // Shown for FR / IA — opt in to running on system-generated derivative assets.
  process_derived_assets?: boolean;
  // Remove-background / print-export addon specific
  output_mode?: 'add_conversion' | 'add_new_asset' | string;
  conversion_name?: string;
  inherit_metadata?: boolean;
  // Print-export specific print params
  format?: string;
  cmyk_profile?: string;
  rendering_intent?: string;
  dpi?: number;
  target_size?: string;
  allow_upscale?: boolean;
};

const PROCESS_DERIVED_ADDONS = new Set(['FACE_RECOGNITION', 'IMAGE_ANALYSIS', 'REMOVE_BACKGROUND']);

const PROCESS_DERIVED_HELPER_TEXT: Record<string, string> = {
  FACE_RECOGNITION:
    'When off, Face Recognition skips system-generated derivative assets like background-removed PNGs. Turn this on only if you also want FR to index faces in background-removed PNG outputs.',
  IMAGE_ANALYSIS:
    'When off, Image Analysis skips system-generated derivative assets. Turn this on only if you also want auto-tags on background-removed PNG outputs.',
  REMOVE_BACKGROUND:
    'When off, Remove Background skips system-generated derivative assets. Keep this off to avoid re-running background removal on already background-removed assets when their metadata is updated.',
};

type EventHooks = {
  disposition?: Disposition;
  'file-saved'?: EventConfig;
  'file-data-updated'?: EventConfig;
  [k: string]: any;
};

type Props = {
  value: string;
  onChange: (next: string) => void;
  showDisposition?: boolean;
  addonKey?: string;
};

const RBG_OUTPUT_MODE_OPTIONS = [
  { label: 'Add as conversion', value: 'add_conversion' },
  { label: 'Add as new asset', value: 'add_new_asset' },
];

// Print Export option values (mirror the backend print engine).
const PRINT_FORMAT_OPTIONS = [
  { label: 'CMYK TIFF (lossless)', value: 'tiff' },
  { label: 'CMYK JPEG', value: 'jpeg' },
  { label: 'Print PDF', value: 'pdf' },
  { label: 'PDF/X-1a', value: 'pdfx' },
];

const PRINT_CMYK_PROFILE_OPTIONS = [
  { label: 'US Web Coated (SWOP)', value: 'USWebCoatedSWOP' },
  { label: 'Coated FOGRA39', value: 'CoatedFOGRA39' },
  { label: 'Coated FOGRA27', value: 'CoatedFOGRA27' },
  { label: 'Uncoated FOGRA29', value: 'UncoatedFOGRA29' },
  { label: 'Web Coated FOGRA28', value: 'WebCoatedFOGRA28' },
  { label: 'US Web Uncoated', value: 'USWebUncoated' },
  { label: 'US Sheetfed Coated', value: 'USSheetfedCoated' },
  { label: 'US Sheetfed Uncoated', value: 'USSheetfedUncoated' },
  { label: 'Japan Color 2001 Coated', value: 'JapanColor2001Coated' },
  { label: 'Japan Color 2001 Uncoated', value: 'JapanColor2001Uncoated' },
];

const PRINT_RENDERING_INTENT_OPTIONS = [
  { label: 'Perceptual', value: 'perceptual' },
  { label: 'Relative colorimetric', value: 'relative_colorimetric' },
  { label: 'Saturation', value: 'saturation' },
  { label: 'Absolute colorimetric', value: 'absolute_colorimetric' },
];

const PRINT_TARGET_SIZE_OPTIONS = [
  { label: 'Original (no resize)', value: '' },
  { label: 'A6', value: 'A6' },
  { label: 'A5', value: 'A5' },
  { label: 'A4', value: 'A4' },
  { label: 'A3', value: 'A3' },
  { label: 'A2', value: 'A2' },
  { label: 'Letter', value: 'letter' },
  { label: 'Legal', value: 'legal' },
  { label: 'Tabloid', value: 'tabloid' },
];

const PRINT_DEFAULT_DPI = 300;

// FR, IA and RBG are all image-only addons, so we hard-code content_types
// rather than asking the user to pick. Kept as a constant so it's easy to
// adjust if a video-capable addon is added later.
const DEFAULT_CONTENT_TYPES = ['image/*'];

const EVENT_LABELS: Record<EventKey, string> = {
  'file-saved': 'When asset is saved',
  'file-data-updated': 'When asset metadata is updated',
};

const EVENT_HINTS: Record<EventKey, string> = {
  'file-saved': 'Run this addon every time a new asset is uploaded.',
  'file-data-updated': 'Run this addon when an existing asset’s metadata is changed.',
};

const getLocalized = (obj?: { [lang: string]: string }): string => {
  if (!obj) return '';
  return obj.en || obj[Object.keys(obj)[0]] || '';
};

const safeParse = (s: string): EventHooks => {
  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const AddonEventHooksForm: React.FC<Props> = ({ value, onChange, showDisposition = true, addonKey }) => {
  const parsed = useMemo(() => safeParse(value), [value]);

  const schemasQuery = useAssetSchemasListQuery('ACTIVE');
  const schemas: AssetSchema[] = useMemo(() => {
    const raw: any = schemasQuery.data;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    return [];
  }, [schemasQuery.data]);

  const isRemoveBackground = addonKey === 'REMOVE_BACKGROUND';
  const isPrintExport = addonKey === 'PRINT_EXPORT';
  const showDispositionSection = showDisposition && (isRemoveBackground || isPrintExport);
  const showProcessDerivedToggle = !!addonKey && PROCESS_DERIVED_ADDONS.has(addonKey);

  // Print options stay folded by default — sensible defaults are applied
  // unless the user opens this and overrides them.
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);

  const emit = (next: EventHooks) => {
    // No addon currently exposes the update_metadata toggle in the UI — backend defaults to true,
    // so strip it before emitting so the saved JSON matches what the user actually sees.
    if (next.disposition && 'update_metadata' in next.disposition) {
      const { update_metadata, ...restDisposition } = next.disposition;
      next = { ...next, disposition: restDisposition };
    }
    // If disposition becomes an empty object, drop it entirely.
    if (next.disposition && Object.keys(next.disposition).length === 0) {
      const { disposition, ...rest } = next;
      next = rest;
    }
    onChange(JSON.stringify(next, null, 2));
  };

  const updateEvent = (ev: EventKey, patch: Partial<EventConfig>) => {
    const next: EventHooks = { ...parsed };
    const prev: EventConfig = next[ev] || {};
    next[ev] = { ...prev, ...patch };
    emit(next);
  };

  const updateDisposition = (patch: Partial<Disposition>) => {
    const next: EventHooks = { ...parsed };
    const merged: Disposition = { ...(next.disposition || {}), ...patch };
    // Strip undefined values so they don't pollute the JSON
    (Object.keys(patch) as Array<keyof Disposition>).forEach((k) => {
      if (patch[k] === undefined) delete merged[k];
    });
    next.disposition = merged;
    emit(next);
  };

  const setSchemaCriteria = (ev: EventKey, sc: SchemaCriteria | undefined) => {
    const next: EventHooks = { ...parsed };
    const prev: EventConfig = next[ev] || {};
    if (sc === undefined) {
      const { schema_criteria, ...rest } = prev;
      next[ev] = rest;
    } else {
      next[ev] = { ...prev, schema_criteria: sc };
    }
    emit(next);
  };

  // Enabling an event auto-populates content_types with the addon's defaults
  // (currently image-only across FR / IA / RBG).
  const setEventEnabled = (ev: EventKey, enabled: boolean) => {
    if (enabled) {
      const existing = parsed[ev]?.content_types;
      const content_types =
        existing && existing.length > 0 ? existing : DEFAULT_CONTENT_TYPES;
      updateEvent(ev, { enabled: true, content_types });
    } else {
      updateEvent(ev, { enabled: false });
    }
  };

  const renderSchemaFieldInput = (fieldName: string, prop: AssetSchemaProperty, currentValue: any, ev: EventKey) => {
    const onFieldChange = (v: any) => {
      const prevSc = parsed[ev]?.schema_criteria || {};
      const prevFields = prevSc.fields || {};
      const nextFields = { ...prevFields };
      if (v === '' || v === undefined || v === null) {
        delete nextFields[fieldName];
      } else {
        nextFields[fieldName] = v;
      }
      setSchemaCriteria(ev, { ...prevSc, fields: nextFields });
    };

    const label = getLocalized(prop.filespin_properties?.title) || prop.title || fieldName;

    if (prop.type === 'boolean') {
      return (
        <div className={styles.fieldRow} key={fieldName}>
          <span className={styles.fieldLabel}>{label}</span>
          <Select
            value={currentValue === true ? 'true' : currentValue === false ? 'false' : ''}
            onChange={(v) => onFieldChange(v === '' ? undefined : v === 'true')}
            options={[
              { label: 'Any', value: '' },
              { label: 'True', value: 'true' },
              { label: 'False', value: 'false' },
            ]}
          />
        </div>
      );
    }

    if (prop.enum && prop.enum.length > 0) {
      return (
        <div className={styles.fieldRow} key={fieldName}>
          <span className={styles.fieldLabel}>{label}</span>
          <Select
            value={currentValue ?? ''}
            onChange={(v) => onFieldChange(v === '' ? undefined : v)}
            options={[{ label: 'Any', value: '' }, ...prop.enum.map((o) => ({ label: o, value: o }))]}
          />
        </div>
      );
    }

    if (prop.type === 'number') {
      return (
        <div className={styles.fieldRow} key={fieldName}>
          <span className={styles.fieldLabel}>{label}</span>
          <Input
            type="number"
            value={currentValue ?? ''}
            onChange={(v) => onFieldChange(v === '' ? undefined : Number(v))}
            placeholder="Any value"
          />
        </div>
      );
    }

    return (
      <div className={styles.fieldRow} key={fieldName}>
        <span className={styles.fieldLabel}>{label}</span>
        <Input
          value={currentValue ?? ''}
          onChange={(v) => onFieldChange(v === '' ? undefined : v)}
          placeholder="Any value"
        />
      </div>
    );
  };

  const renderEvent = (ev: EventKey) => {
    const cfg = parsed[ev] || {};
    const sc = cfg.schema_criteria;
    const selectedSchemaId = sc?.data_schema_id;
    const selectedSchema = schemas.find((s) => s.id === selectedSchemaId);
    const fieldValues = sc?.fields || {};

    return (
      <section className={cn(styles.section, { [styles.sectionActive]: !!cfg.enabled })} key={ev}>
        <header className={styles.sectionHeader}>
          <div>
            <h5 className={styles.sectionTitle}>{EVENT_LABELS[ev]}</h5>
            <p className={styles.sectionHint}>{EVENT_HINTS[ev]}</p>
          </div>
          <ReactSwitch
            checked={!!cfg.enabled}
            onChange={(checked: boolean) => setEventEnabled(ev, checked)}
            checkedIcon={false}
            uncheckedIcon={false}
            onColor="#2e53e3"
            width={40}
            height={20}
            handleDiameter={16}
          />
        </header>

        {cfg.enabled && (
          <div className={styles.sectionBody}>
            <div className={styles.group}>
              <div className={styles.groupLabel}>
                Schema criteria
                <span className={styles.groupHint}>
                  Only trigger when the asset's metadata matches the selected schema fields.
                </span>
              </div>

              <Select
                value={selectedSchemaId != null ? String(selectedSchemaId) : ''}
                onChange={(v) => {
                  if (!v) {
                    setSchemaCriteria(ev, undefined);
                  } else {
                    setSchemaCriteria(ev, { data_schema_id: Number(v), fields: {} });
                  }
                }}
                options={[
                  { label: schemasQuery.isLoading ? 'Loading schemas…' : 'No schema criteria', value: '' },
                  ...schemas.map((s) => ({
                    label: getLocalized(s.name) || `Schema ${s.id}`,
                    value: String(s.id),
                  })),
                ]}
              />

              {selectedSchema && (
                <div className={styles.fields}>
                  {Object.entries(selectedSchema.schema?.properties || {}).map(([fieldName, prop]) =>
                    renderSchemaFieldInput(fieldName, prop as AssetSchemaProperty, fieldValues[fieldName], ev),
                  )}
                  {Object.keys(selectedSchema.schema?.properties || {}).length === 0 && (
                    <div className={styles.emptyFields}>This schema has no fields.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className={styles.wrapper}>
      {/* RBG / Print Export: how to save the result. Always shown (mandatory choice), so render it active. */}
      {showDispositionSection && (
        <section className={cn(styles.section, styles.sectionActive)}>
          <header className={styles.sectionHeader}>
            <div>
              <h5 className={styles.sectionTitle}>Disposition</h5>
              <p className={styles.sectionHint}>
                {isPrintExport
                  ? 'How the generated print file should be saved.'
                  : 'How the background-removed image should be saved.'}
                <br />
                Metadata from original asset can be inherited when saving as a new asset.
              </p>
            </div>
          </header>
          <div className={styles.sectionBody}>
            <div className={styles.group}>
              <div className={styles.groupLabel}>Output mode</div>
              <Select
                value={parsed.disposition?.output_mode || ''}
                onChange={(v) => {
                  const patch: Partial<Disposition> = { output_mode: v || undefined };
                  // Clear dependent fields when switching modes
                  if (v !== 'add_conversion') patch.conversion_name = undefined;
                  if (v !== 'add_new_asset') patch.inherit_metadata = undefined;
                  updateDisposition(patch);
                }}
                options={[{ label: 'Select…', value: '' }, ...RBG_OUTPUT_MODE_OPTIONS]}
              />

              {parsed.disposition?.output_mode === 'add_conversion' && (
                <div className={styles.fields}>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Conversion name</span>
                    <Input
                      value={parsed.disposition?.conversion_name ?? ''}
                      onChange={(v) => updateDisposition({ conversion_name: v === '' ? undefined : v })}
                      placeholder={isPrintExport ? 'e.g. print_ready' : 'e.g. background_removed'}
                    />
                  </div>
                </div>
              )}

              {parsed.disposition?.output_mode === 'add_new_asset' && (
                <div className={styles.fields}>
                  <Checkbox
                    label="Inherit metadata from source asset"
                    checked={!!parsed.disposition?.inherit_metadata}
                    onChange={(e) => updateDisposition({ inherit_metadata: e.target.checked || undefined })}
                  />
                </div>
              )}
            </div>

            {/* Print-export-specific print parameters — folded by default. */}
            {isPrintExport && (
              <div className={styles.group}>
                <button
                  type="button"
                  className={styles.advancedToggle}
                  aria-expanded={printOptionsOpen}
                  onClick={() => setPrintOptionsOpen((o) => !o)}
                >
                  {printOptionsOpen ? <CaretDown /> : <CaretRight />}
                  Advanced print options
                </button>
                <p className={styles.defaultsNote}>
                  Defaults are tuned for the best quality and broadest printer compatibility — CMYK TIFF
                  (lossless), US Web Coated (SWOP), relative colorimetric, 300&nbsp;DPI, original size. You can change the defaults if your print provider requires specific settings.
                </p>
                {printOptionsOpen && (
                  <div className={styles.fields}>
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabelCell}>
                        <span className={styles.fieldLabel}>Format</span>
                        <span className={styles.fieldHint}>
                          Output file type. TIFF (lossless) is the recommended prepress default; JPEG is smaller;
                          PDF / PDF&#8209;X produce print-ready documents.
                        </span>
                      </span>
                      <Select
                        value={parsed.disposition?.format || 'tiff'}
                        onChange={(v) => updateDisposition({ format: v || undefined })}
                        options={PRINT_FORMAT_OPTIONS}
                      />
                    </div>
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabelCell}>
                        <span className={styles.fieldLabel}>CMYK profile</span>
                        <span className={styles.fieldHint}>
                          Target press colour space. US Web Coated (SWOP) is the most widely supported —
                          recommended unless your printer specifies another.
                        </span>
                      </span>
                      <Select
                        value={parsed.disposition?.cmyk_profile || 'USWebCoatedSWOP'}
                        onChange={(v) => updateDisposition({ cmyk_profile: v || undefined })}
                        options={PRINT_CMYK_PROFILE_OPTIONS}
                      />
                    </div>
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabelCell}>
                        <span className={styles.fieldLabel}>Rendering intent</span>
                        <span className={styles.fieldHint}>
                          How out-of-gamut colours are mapped to CMYK. Relative colorimetric (recommended) keeps
                          accurate in-gamut colours; Perceptual suits vivid photos.
                        </span>
                      </span>
                      <Select
                        value={parsed.disposition?.rendering_intent || 'relative_colorimetric'}
                        onChange={(v) => updateDisposition({ rendering_intent: v || undefined })}
                        options={PRINT_RENDERING_INTENT_OPTIONS}
                      />
                    </div>
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabelCell}>
                        <span className={styles.fieldLabel}>DPI</span>
                        <span className={styles.fieldHint}>
                          Print resolution. 300&nbsp;DPI is the standard for high-quality print.
                        </span>
                      </span>
                      <Input
                        type="number"
                        value={parsed.disposition?.dpi ?? PRINT_DEFAULT_DPI}
                        onChange={(v) => updateDisposition({ dpi: v === '' ? undefined : Number(v) })}
                        placeholder="300"
                      />
                    </div>
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabelCell}>
                        <span className={styles.fieldLabel}>Target size</span>
                        <span className={styles.fieldHint}>
                          Resize to a standard page size, or keep the original dimensions.
                        </span>
                      </span>
                      <Select
                        value={parsed.disposition?.target_size || ''}
                        onChange={(v) => {
                          const patch: Partial<Disposition> = { target_size: v || undefined };
                          // Upscaling only applies when resizing to a target.
                          if (!v) patch.allow_upscale = undefined;
                          updateDisposition(patch);
                        }}
                        options={PRINT_TARGET_SIZE_OPTIONS}
                      />
                    </div>
                    {parsed.disposition?.target_size && (
                      <Checkbox
                        label="Allow AI upscaling if the source is too small (uses credits)"
                        checked={!!parsed.disposition?.allow_upscale}
                        onChange={(e) => updateDisposition({ allow_upscale: e.target.checked || undefined })}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Trigger conditions */}
      {(['file-saved', 'file-data-updated'] as EventKey[]).map(renderEvent)}

      {/* Process derived assets — bottom slider for FR / IA */}
      {showProcessDerivedToggle && (
        <section
          className={cn(styles.section, {
            [styles.sectionActive]: !!parsed.disposition?.process_derived_assets,
          })}
        >
          <header className={styles.sectionHeader}>
            <div>
              <h5 className={styles.sectionTitle}>Process derived assets</h5>
              <p className={styles.sectionHint}>
                {PROCESS_DERIVED_HELPER_TEXT[addonKey || ''] ||
                  'When off, this addon skips system-generated derivative assets.'}
              </p>
            </div>
            <ReactSwitch
              checked={!!parsed.disposition?.process_derived_assets}
              onChange={(checked: boolean) => updateDisposition({ process_derived_assets: checked })}
              checkedIcon={false}
              uncheckedIcon={false}
              onColor="#2e53e3"
              width={40}
              height={20}
              handleDiameter={16}
            />
          </header>
        </section>
      )}
    </div>
  );
};

export default AddonEventHooksForm;
